/**
 * Sync Orchestrator
 *
 * Sets up subscriptions between HVAC store and IDF store for automatic
 * bidirectional synchronization. Handles debouncing and prevents sync loops.
 */

import { useHVACStore } from '../hvacStore';
import { useIDFStore } from '../../stores/idfStore';
import { useSyncStore } from './syncStore';
import { findHVACObjects } from './syncService';

// Track if we're currently in a sync operation to prevent infinite loops
// These flags are set when a sync is SCHEDULED (not just when executing)
// to prevent race conditions during the debounce window
let isSyncingFromGraph = false;
let isSyncingFromIDF = false;

// Debounce timers
let graphSyncTimer: ReturnType<typeof setTimeout> | null = null;
let idfSyncTimer: ReturnType<typeof setTimeout> | null = null;

// Pending changes accumulated during debounce
let pendingGraphChanges: {
  addedNodes: Array<{ id: string }>;
  removedNodes: Array<{ id: string }>;
  updatedNodes: Array<{ id: string }>;
  addedEdges: Array<{ id: string }>;
  removedEdges: Array<{ id: string }>;
} | null = null;

// Pending IDF document change
let pendingIDFSync: {
  newDocument: ReturnType<typeof useIDFStore.getState>['document'];
  prevDocument: ReturnType<typeof useIDFStore.getState>['document'];
} | null = null;

/**
 * Initialize the sync orchestrator with subscriptions
 */
export function initializeSyncOrchestrator(): () => void {
  const unsubscribers: Array<() => void> = [];

  // Subscribe to HVAC store changes
  const unsubHVAC = useHVACStore.subscribe((state, prevState) => {
    if (isSyncingFromIDF) return;

    const syncStore = useSyncStore.getState();
    if (!syncStore.enabled || !syncStore.config.autoSyncFromGraph) return;

    // Detect node changes
    const addedNodes = state.nodes.filter(
      (n) => !prevState.nodes.find((pn) => pn.id === n.id)
    );
    const removedNodes = prevState.nodes.filter(
      (pn) => !state.nodes.find((n) => n.id === pn.id)
    );
    const updatedNodes = state.nodes.filter((n) => {
      const prev = prevState.nodes.find((pn) => pn.id === n.id);
      if (!prev) return false;
      return (
        prev.data.name !== n.data.name ||
        JSON.stringify(prev.data.fields) !== JSON.stringify(n.data.fields)
      );
    });

    // Detect edge changes
    const addedEdges = state.edges.filter(
      (e) => !prevState.edges.find((pe) => pe.id === e.id)
    );
    const removedEdges = prevState.edges.filter(
      (pe) => !state.edges.find((e) => e.id === pe.id)
    );

    // Handle changes with debouncing
    if (
      addedNodes.length > 0 ||
      removedNodes.length > 0 ||
      updatedNodes.length > 0 ||
      addedEdges.length > 0 ||
      removedEdges.length > 0
    ) {
      debouncedGraphSync({
        addedNodes,
        removedNodes,
        updatedNodes,
        addedEdges,
        removedEdges,
      });
    }
  });
  unsubscribers.push(unsubHVAC);

  // Subscribe to IDF store changes
  const unsubIDF = useIDFStore.subscribe((state, prevState) => {
    if (isSyncingFromGraph) return;

    const syncStore = useSyncStore.getState();
    if (!syncStore.enabled || !syncStore.config.autoSyncFromIDF) return;

    // Detect document changes
    if (state.document !== prevState.document && state.document) {
      debouncedIDFSync(state.document, prevState.document);
    }
  });
  unsubscribers.push(unsubIDF);

  // Return cleanup function
  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
    if (graphSyncTimer) clearTimeout(graphSyncTimer);
    if (idfSyncTimer) clearTimeout(idfSyncTimer);
  };
}

/**
 * Debounced sync from graph to IDF
 * Sets the sync flag immediately to prevent race conditions during debounce window
 */
function debouncedGraphSync(changes: {
  addedNodes: Array<{ id: string }>;
  removedNodes: Array<{ id: string }>;
  updatedNodes: Array<{ id: string }>;
  addedEdges: Array<{ id: string }>;
  removedEdges: Array<{ id: string }>;
}) {
  const config = useSyncStore.getState().config;

  // Set flag immediately when scheduling to prevent IDF->Graph sync during debounce
  isSyncingFromGraph = true;

  // Accumulate changes during debounce window
  if (pendingGraphChanges) {
    // Merge changes, avoiding duplicates by id
    const mergeById = <T extends { id: string }>(existing: T[], incoming: T[]): T[] => {
      const ids = new Set(existing.map(item => item.id));
      return [...existing, ...incoming.filter(item => !ids.has(item.id))];
    };
    pendingGraphChanges = {
      addedNodes: mergeById(pendingGraphChanges.addedNodes, changes.addedNodes),
      removedNodes: mergeById(pendingGraphChanges.removedNodes, changes.removedNodes),
      updatedNodes: mergeById(pendingGraphChanges.updatedNodes, changes.updatedNodes),
      addedEdges: mergeById(pendingGraphChanges.addedEdges, changes.addedEdges),
      removedEdges: mergeById(pendingGraphChanges.removedEdges, changes.removedEdges),
    };
  } else {
    pendingGraphChanges = { ...changes };
  }

  if (graphSyncTimer) {
    clearTimeout(graphSyncTimer);
  }

  graphSyncTimer = setTimeout(() => {
    const changesToSync = pendingGraphChanges;
    pendingGraphChanges = null;
    if (changesToSync) {
      performGraphToIDFSync(changesToSync);
    } else {
      isSyncingFromGraph = false;
    }
  }, config.debounceMs);
}

/**
 * Perform sync from graph to IDF
 * Note: isSyncingFromGraph is already set by debouncedGraphSync
 */
function performGraphToIDFSync(changes: {
  addedNodes: Array<{ id: string }>;
  removedNodes: Array<{ id: string }>;
  updatedNodes: Array<{ id: string }>;
  addedEdges: Array<{ id: string }>;
  removedEdges: Array<{ id: string }>;
}) {
  try {
    const syncStore = useSyncStore.getState();

    // Handle removed nodes first
    for (const node of changes.removedNodes) {
      syncStore.handleNodeDeleted(node.id);
    }

    // Handle removed edges
    for (const edge of changes.removedEdges) {
      syncStore.handleEdgeDeleted(edge.id);
    }

    // Handle added nodes
    for (const node of changes.addedNodes) {
      syncStore.syncNodeToIDF(node.id);
    }

    // Handle updated nodes
    for (const node of changes.updatedNodes) {
      syncStore.syncNodeToIDF(node.id);
    }

    // Handle added edges (update port node names)
    for (const edge of changes.addedEdges) {
      syncStore.syncEdgeToIDF(edge.id);
    }
  } finally {
    isSyncingFromGraph = false;
  }
}

/**
 * Debounced sync from IDF to graph
 * Sets the sync flag immediately to prevent race conditions during debounce window
 */
function debouncedIDFSync(
  newDocument: ReturnType<typeof useIDFStore.getState>['document'],
  prevDocument: ReturnType<typeof useIDFStore.getState>['document']
) {
  const config = useSyncStore.getState().config;

  // Set flag immediately when scheduling to prevent Graph->IDF sync during debounce
  isSyncingFromIDF = true;

  // Store the latest document state (always use the most recent)
  pendingIDFSync = { newDocument, prevDocument };

  if (idfSyncTimer) {
    clearTimeout(idfSyncTimer);
  }

  idfSyncTimer = setTimeout(() => {
    const syncData = pendingIDFSync;
    pendingIDFSync = null;
    if (syncData) {
      performIDFToGraphSync(syncData.newDocument, syncData.prevDocument);
    } else {
      isSyncingFromIDF = false;
    }
  }, config.debounceMs);
}

/**
 * Perform sync from IDF to graph
 * Note: isSyncingFromIDF is already set by debouncedIDFSync
 */
function performIDFToGraphSync(
  newDocument: ReturnType<typeof useIDFStore.getState>['document'],
  prevDocument: ReturnType<typeof useIDFStore.getState>['document']
) {
  if (!newDocument) {
    isSyncingFromIDF = false;
    return;
  }

  try {
    const syncStore = useSyncStore.getState();

    // Find HVAC objects in both documents
    const newHVACObjects = findHVACObjects(newDocument.objects);
    const prevHVACObjects = prevDocument ? findHVACObjects(prevDocument.objects) : [];

    // Detect added objects
    const addedObjects = newHVACObjects.filter(
      (obj) => !prevHVACObjects.find((po) => po.id === obj.id)
    );

    // Detect removed objects
    const removedObjects = prevHVACObjects.filter(
      (po) => !newHVACObjects.find((obj) => obj.id === po.id)
    );

    // Detect updated objects
    const updatedObjects = newHVACObjects.filter((obj) => {
      const prev = prevHVACObjects.find((po) => po.id === obj.id);
      if (!prev) return false;
      return JSON.stringify(obj.fields) !== JSON.stringify(prev.fields);
    });

    // Handle removed objects
    for (const obj of removedObjects) {
      syncStore.handleObjectDeleted(obj.id);
    }

    // Handle added objects (only if config allows creating nodes)
    if (syncStore.config.createMissingNodes) {
      for (const obj of addedObjects) {
        if (!syncStore.isObjectSynced(obj.id)) {
          syncStore.syncObjectToGraph(obj.id);
        }
      }
    }

    // Handle updated objects
    for (const obj of updatedObjects) {
      if (syncStore.isObjectSynced(obj.id)) {
        syncStore.syncObjectToGraph(obj.id);
      }
    }
  } finally {
    isSyncingFromIDF = false;
  }
}

/**
 * Manually trigger a full sync from graph to IDF
 */
export function syncGraphToIDF(): void {
  isSyncingFromGraph = true;
  try {
    useSyncStore.getState().syncAllNodesToIDF();
  } finally {
    isSyncingFromGraph = false;
  }
}

/**
 * Manually trigger a full sync from IDF to graph
 */
export function syncIDFToGraph(): void {
  isSyncingFromIDF = true;
  try {
    useSyncStore.getState().syncAllObjectsToGraph();
  } finally {
    isSyncingFromIDF = false;
  }
}

/**
 * Manually trigger initial sync (matches existing objects by name)
 */
export function performInitialSync(): void {
  isSyncingFromGraph = true;
  isSyncingFromIDF = true;
  try {
    useSyncStore.getState().performInitialSync();
  } finally {
    isSyncingFromGraph = false;
    isSyncingFromIDF = false;
  }
}

/**
 * Check if currently syncing (to prevent UI updates during sync)
 */
export function isSyncing(): boolean {
  return isSyncingFromGraph || isSyncingFromIDF;
}
